import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  Crown, Check, Sparkles, Ban, Camera, ChefHat, 
  CalendarRange, ShoppingCart, ArrowLeft, Copy, Users, Gift, Clock, Loader2, AlertTriangle, Music
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ErrorModal from '../components/ErrorModal';
import StripePaymentModal from '../components/StripePaymentModal';
import { API_URL } from '../config/api';
import { usePremiumStatus, useInvalidatePremiumStatus } from '../lib/queries';
import { usePaymentsConfig, getPaymentPlatform, pickProvider } from '../lib/paymentPlatform';
import { rcLogin, rcPurchase, rcRestore, syncWithBackend, isRevenueCatAvailable, rcGetProducts, rcGetOfferings } from '../lib/revenuecat';
import { hapticSuccess, hapticError } from '../lib/haptics';

// StoreKit product identifiers (must match App Store Connect and the
// products attached to the RevenueCat offering). These are the SAME
// IDs we query StoreKit for via `rcGetProducts` — bypassing the
// RevenueCat dashboard's anonymous fallback storefront entirely.
const STORE_PRODUCT_IDS = {
  monthly: 'slimmy_premium_monthly',
  annual:  'slimmy_premium_yearly',
};

// Italy fallback prices — used ONLY when:
//   1. platform is iOS native, AND
//   2. device locale starts with `it-` (Italian Apple ID), AND
//   3. StoreKit / RevenueCat SDK returns a non-EUR currency (USD)
// These values mirror what is configured in App Store Connect for the
// Italian storefront and match what Apple actually shows in the
// payment sheet. They are deliberately NEVER used to bill the user
// (Apple controls billing) — they only patch the UI so the displayed
// price matches the Apple checkout popup.
const ITALY_FALLBACK_PRICES = {
  monthly: {
    price: 4.99,
    priceString: '4,99 €',
    currencyCode: 'EUR',
    productIdentifier: 'slimmy_premium_monthly',
    subscriptionPeriod: 'P1M',
    _italyFallback: true,
  },
  annual: {
    price: 44.99,
    priceString: '44,99 €',
    currencyCode: 'EUR',
    productIdentifier: 'slimmy_premium_yearly',
    subscriptionPeriod: 'P1Y',
    _italyFallback: true,
  },
};

// True when the device's preferred language is Italian — used as a
// guard for the Italian fallback prices above. Reads navigator.language
// and navigator.languages so any IT locale (it-IT, it-CH, it) matches.
const isItalianLocale = () => {
  try {
    const lang = (navigator.language || '').toLowerCase();
    const list = (navigator.languages || []).map((l) => (l || '').toLowerCase());
    return lang.startsWith('it') || list.some((l) => l.startsWith('it'));
  } catch (_) {
    return false;
  }
};

// Default prices if not configured
const DEFAULT_MONTHLY_PRICE = 4.99;
const DEFAULT_ANNUAL_PRICE = 39.99;

// Helper per formattare i prezzi con 2 decimali (es: 19.9 -> 19.90)
const formatPrice = (price) => {
  if (price === null || price === undefined || isNaN(price)) return '0.00';
  return Number(price).toFixed(2);
};

// Features will be built with translations inside the component

// Countdown helper
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));
  
  useEffect(() => {
    if (!targetDate) return;
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
}

function calculateTimeLeft(targetDate) {
  if (!targetDate) return null;
  const diff = new Date(targetDate) - new Date();
  if (diff <= 0) return null;
  
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60)
  };
}

export default function Premium() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Premium status — shared React Query hook (dedup'd across app).
  const { data: status } = usePremiumStatus();
  const invalidatePremium = useInvalidatePremiumStatus();
  const [checkingOut, setCheckingOut] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [copied, setCopied] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentPlanType, setPaymentPlanType] = useState(null);

  // Promo code state (applied via debounce 500ms)
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // { valid, final_price, amount_saved, ... } | { error }
  // Admin can globally disable the promo code field at checkout
  // (defaults to true to preserve current behaviour). Read from the
  // PUBLIC settings endpoint — no auth required so it works for
  // anonymous Premium page views too.
  const [promoCodesEnabled, setPromoCodesEnabled] = useState(true);
  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/settings/public`)
      .then(r => {
        if (active && r.data?.promo_codes_enabled === false) {
          setPromoCodesEnabled(false);
        }
      })
      .catch(() => { /* default true */ });
    return () => { active = false; };
  }, []);
  
  // Currency state based on user location
  const [userCurrency, setUserCurrency] = useState('eur');
  const [userCountry, setUserCountry] = useState('IT');

  // Live StoreKit / Play Billing prices fetched from RevenueCat. When
  // running inside the iOS/Android Capacitor wrapper, the price the
  // user is actually charged is set in App Store Connect / Play Console
  // — NOT in our admin Settings. We MUST display that exact price in
  // the UI (Apple App Review will reject the app otherwise). Format:
  //   { monthly: { price, priceString, currencyCode, subscriptionPeriod },
  //     annual:  { price, priceString, currencyCode, subscriptionPeriod } }
  // Defaults to null while loading (or on web — where we keep the
  // admin-configured Stripe prices).
  const [storePrices, setStorePrices] = useState(null);
  // Set to a human-readable message when we couldn't fetch the live
  // store products from RevenueCat on iOS/Android — required by the
  // product spec to surface a clear error (instead of silently
  // falling back to hardcoded prices) when the native side has not
  // yet been wired up correctly (RC offering missing, product IDs
  // wrong in Play Console, public API key not set, etc.).
  const [priceFetchError, setPriceFetchError] = useState(null);
  // True from the moment the page mounts on iOS/Android until the
  // RevenueCat offerings call resolves (success OR failure). Used to
  // render a spinner instead of a wrong/fallback price during the
  // brief StoreKit fetch.
  const [pricesLoading, setPricesLoading] = useState(false);

  // Payment provider routing — pick between Stripe / Apple IAP /
  // Google Play based on the runtime platform and the admin toggles.
  // On iOS / Android wrappers we MUST NOT show Stripe per App Store
  // and Play Store policies.
  const { data: paymentsConfig } = usePaymentsConfig();
  const paymentPlatform = getPaymentPlatform();
  const activeProvider = pickProvider(paymentPlatform, paymentsConfig?.providers);
  const platformConfigured =
    paymentPlatform === 'ios-app'
      ? paymentsConfig?.ios?.configured
      : paymentPlatform === 'android-app'
      ? paymentsConfig?.android?.configured
      : true; // web: Stripe is always "ready" if toggled on
  
  // Error modal state
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', errorCode: '' });
  
  const showError = (title, message, errorCode = '') => {
    setErrorModal({ isOpen: true, title, message, errorCode });
  };
  
  // European countries that use EUR
  const EUR_COUNTRIES = [
    'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 
    'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', 'AD', 'MC', 
    'SM', 'VA', 'ME', 'XK'
  ];
  
  // Get currency symbol
  const getCurrencySymbol = () => userCurrency === 'usd' ? '$' : '€';
  
  // Get current price based on currency (considers active offers)
  const getPrice = (type) => {
    // On iOS/Android, the StoreKit/Play price is the SOURCE OF TRUTH.
    // We must show exactly what Apple/Google will charge — required by
    // App Store Review Guideline 3.1.2 (and Play equivalent).
    if (storePrices?.[type]?.price != null) {
      return storePrices[type].price;
    }

    if (!config) return type === 'monthly' ? DEFAULT_MONTHLY_PRICE : DEFAULT_ANNUAL_PRICE;
    
    // Check if there's an active offer for this plan type
    const offer = config?.offer;
    const countdown = offer?.expires_at ? (new Date(offer.expires_at) > new Date() ? true : null) : null;
    
    if (offer && countdown) {
      if (type === 'monthly' && offer.monthly?.enabled) {
        return offer.monthly.price;
      }
      if (type === 'annual' && offer.annual?.enabled) {
        return offer.annual.price;
      }
    }
    
    // Regular pricing based on currency
    if (userCurrency === 'usd') {
      return type === 'monthly' 
        ? (config.monthly_price_usd || DEFAULT_MONTHLY_PRICE)
        : (config.annual_price_usd || DEFAULT_ANNUAL_PRICE);
    } else {
      return type === 'monthly' 
        ? (config.monthly_price || DEFAULT_MONTHLY_PRICE)
        : (config.annual_price || DEFAULT_ANNUAL_PRICE);
    }
  };

  // Localised price string for display. We ALWAYS use the raw
  // `priceString` provided by RevenueCat / StoreKit — that is the
  // EXACT same string the Apple payment sheet shows. We never reformat
  // it with the app's language/locale, otherwise we'd risk showing a
  // value that doesn't match what Apple will charge.
  const getPriceString = (type) => {
    const entry = storePrices?.[type];
    if (entry?.priceString) return entry.priceString;
    // Web/Stripe fallback only — never reached on native iOS/Android
    // when storePrices has been populated by RevenueCat.
    return `${getCurrencySymbol()}${formatPrice(getPrice(type))}`;
  };

  // Running inside the native iOS/Android wrapper — we MUST source
  // prices from StoreKit/Play, no admin fallback allowed.
  const isNativeStore = paymentPlatform === 'ios-app' || paymentPlatform === 'android-app';

  // The price for `type` is ready to render:
  //  - on web, always (we use admin-configured prices)
  //  - on native, only once RC offerings have been fetched and the
  //    specific package (monthly/annual) is present
  const isPriceReady = (type) => {
    if (!isNativeStore) return true;
    return storePrices?.[type]?.priceString != null;
  };

  // Per-month equivalent of the annual price. ALWAYS computed from
  // RevenueCat data (annual.price / 12 formatted with annual.currencyCode);
  // never derived from the app language, IP country, admin pricing or
  // any other client-side guess. If RC didn't give us BOTH numeric
  // price AND currencyCode we return null and the caller hides the row.
  // Locale is intentionally left as `undefined` so Intl uses the device's
  // default — same source StoreKit uses for its own priceString, so the
  // formatting (decimal separator, symbol position) stays consistent.
  const getPerMonthString = () => {
    const annual = storePrices?.annual;
    if (annual?.price == null || !annual?.currencyCode) return null;
    const perMonth = annual.price / 12;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: annual.currencyCode,
      }).format(perMonth);
    } catch {
      return null;
    }
  };

  // Annual-vs-monthly discount, computed dynamically from the live
  // RevenueCat / StoreKit prices. Returns an integer percentage (e.g.
  // 37) or null when either price is missing — never falls back to a
  // hardcoded "save N months" string.
  //
  //   expectedAnnual = monthly.price * 12
  //   discount      = (expectedAnnual - annual.price) / expectedAnnual * 100
  //
  // We require BOTH RC prices AND matching currencyCodes — comparing
  // 5,99 € to $39.99 is meaningless, so on a currency mismatch we hide
  // the badge entirely rather than show a wrong percent.
  const getAnnualDiscountPercent = () => {
    const monthly = storePrices?.monthly;
    const annual  = storePrices?.annual;
    if (!monthly?.price || !annual?.price) return null;
    if (!monthly.currencyCode || !annual.currencyCode) return null;
    if (monthly.currencyCode !== annual.currencyCode) return null;
    const expected = monthly.price * 12;
    if (annual.price >= expected) return null;
    return Math.round(((expected - annual.price) / expected) * 100);
  };
  
  // Detect user country on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Try to get country from free IP geolocation service
        const res = await fetch('https://ipapi.co/json/', { timeout: 3000 });
        if (res.ok) {
          const data = await res.json();
          const country = data.country_code || 'IT';
          setUserCountry(country);
          setUserCurrency(EUR_COUNTRIES.includes(country) ? 'eur' : 'usd');
          console.log(`Detected country: ${country}, currency: ${EUR_COUNTRIES.includes(country) ? 'EUR' : 'USD'}`);
        }
      } catch (err) {
        // Fallback: check browser language
        const browserLang = navigator.language || navigator.userLanguage || 'it';
        const langCountry = browserLang.split('-')[1]?.toUpperCase() || 
                           (browserLang.toLowerCase().startsWith('en') ? 'US' : 'IT');
        setUserCountry(langCountry);
        setUserCurrency(EUR_COUNTRIES.includes(langCountry) ? 'eur' : 'usd');
        console.log(`Fallback country from browser: ${langCountry}`);
      }
    };
    detectCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the LIVE StoreKit / Play Billing prices directly via
  // `Purchases.getProducts()` on iOS / Android. We query StoreKit on
  // the device (NOT the RevenueCat offering, which can return the
  // dashboard's fallback storefront for anonymous users). The
  // resulting `priceString` is the EXACT same string Apple will show
  // in its payment sheet — required by App Store Review Guideline
  // 3.1.2. On Web (Stripe) we don't fetch — the admin-configured
  // Stripe prices stay in effect.
  useEffect(() => {
    if (paymentPlatform !== 'ios-app' && paymentPlatform !== 'android-app') return;
    let cancelled = false;
    setPricesLoading(true);

    // Robust extraction. The Capacitor RC plugin exposes prices with
    // slightly different shapes across versions (camelCase vs snake_case,
    // top-level vs nested under `defaultOption.pricingPhases[0].price`).
    // We probe all known field names so we always get the same EXACT
    // strings that StoreKit / Play Billing emit — the very strings shown
    // in the Apple payment popup. We do NOT synthesize a priceString
    // ourselves from price+currencyCode (would risk a value different
    // from what Apple charges).
    const pickProductFields = (product) => {
      if (!product) return null;
      const phase = product?.defaultOption?.pricingPhases?.[0]?.price
        || product?.pricingPhases?.[0]?.price
        || null;
      const price = (
        product.price
        ?? product.priceAmount
        ?? phase?.amount
        ?? (phase?.amountMicros && phase.amountMicros / 1_000_000)
        ?? null
      );
      const currencyCode = (
        product.currencyCode
        ?? product.currency
        ?? phase?.currencyCode
        ?? null
      );
      const priceString = (
        product.priceString
        ?? product.localizedPriceString
        ?? product.localizedPrice
        ?? product.price_string
        ?? phase?.formatted
        ?? null
      );
      if (price == null && !priceString) return null;
      return {
        price,
        priceString,
        currencyCode,
        productIdentifier: product.identifier || product.productIdentifier || null,
        subscriptionPeriod: product.subscriptionPeriod
          || product.subscription_period
          || product?.defaultOption?.subscriptionPeriod
          || null,
      };
    };

    // Primary path: read prices from the RevenueCat OFFERING. This is
    // platform-agnostic and works for both Apple StoreKit and Google
    // Play Billing v5+ (where product IDs include the base-plan suffix
    // `slimmy_premium_monthly:monthly`). The offering's `monthly` /
    // `annual` packages already encapsulate the platform-specific IDs.
    const tryFetchFromOffering = async () => {
      const offering = await rcGetOfferings();
      if (!offering) {
        console.warn('[Premium] No current offering returned by RevenueCat');
        return { monthly: null, annual: null };
      }
      const m = offering.monthly?.product || null;
      const a = offering.annual?.product || null;
      console.log('[Premium] Offering loaded:', offering.identifier,
        '| monthly:', m?.identifier, m?.priceString,
        '| annual:', a?.identifier, a?.priceString);
      return {
        monthly: pickProductFields(m),
        annual:  pickProductFields(a),
      };
    };

    // Fallback path: direct `getProducts` by hardcoded ID. Only used if
    // the offering route fails (e.g. RC dashboard not configured yet).
    const tryFetchByIds = async () => {
      const productIds = [STORE_PRODUCT_IDS.monthly, STORE_PRODUCT_IDS.annual];
      const products = await rcGetProducts(productIds);
      console.log('[Premium] getProducts returned', products?.length, 'items for IDs', productIds);
      const byId = {};
      for (const p of products || []) {
        const id = p?.identifier || p?.productIdentifier;
        if (id) byId[id] = p;
      }
      return {
        monthly: pickProductFields(byId[STORE_PRODUCT_IDS.monthly]),
        annual:  pickProductFields(byId[STORE_PRODUCT_IDS.annual]),
      };
    };

    const tryFetch = async () => {
      const fromOffering = await tryFetchFromOffering();
      if (fromOffering.monthly || fromOffering.annual) return fromOffering;
      return tryFetchByIds();
    };

    (async () => {
      if (!isRevenueCatAvailable()) {
        if (!cancelled) setPricesLoading(false);
        return;
      }
      try {
        // First attempt — StoreKit will normally answer immediately.
        let next = await tryFetch();
        // Single retry for cold-start race where StoreKit hasn't
        // finished resolving product metadata yet (priceString still
        // missing). NO currency/country fallback here — StoreKit is
        // the primary source of truth.
        const missingString = !next?.annual?.priceString || !next?.monthly?.priceString;
        if (!cancelled && missingString) {
          await new Promise((r) => setTimeout(r, 1500));
          if (cancelled) return;
          const retry = await tryFetch();
          if (retry?.annual?.priceString || retry?.monthly?.priceString) {
            next = retry;
          }
        }

        // Italy fallback. The RC/StoreKit SDK has been observed
        // returning USD on Italian Apple IDs (anonymous appUserID
        // routed to the dashboard's US fallback storefront) even though
        // the Apple checkout popup correctly shows EUR. When we detect
        // an Italian device locale AND the SDK currency is not EUR,
        // we patch the UI with the App Store Connect Italian prices so
        // the displayed amount matches the Apple payment sheet. This
        // is a UI-only override — purchasePackage() is untouched and
        // Apple controls the actual billing currency / amount.
        // IMPORTANT: this fallback is iOS-ONLY (see isIos guard) so it
        // never affects the Android / Google Play Billing path.
        const isIos = paymentPlatform === 'ios-app';
        const skCurrency = next?.annual?.currencyCode || next?.monthly?.currencyCode;
        const wrongCurrency = !skCurrency || skCurrency !== 'EUR';
        if (isIos && isItalianLocale() && wrongCurrency) {
          console.warn('[Premium] StoreKit SDK returned', skCurrency || '(nothing)',
            '— applying Italian fallback prices (UI-only, Apple billing unchanged).');
          next = ITALY_FALLBACK_PRICES;
        }

        if (cancelled) return;
        if (next.monthly || next.annual) {
          setStorePrices(next);
          setPriceFetchError(null);
        } else {
          // No products returned. On Android this almost always means
          // the Google Play product IDs (slimmy_premium_monthly /
          // slimmy_premium_yearly) are missing or not attached to a
          // RevenueCat offering. Surface a clear error and rely on the
          // diagnostic tab for the full report — do NOT show hardcoded
          // prices that would mismatch the Google Play checkout.
          const platformLabel = paymentPlatform === 'android-app' ? 'Google Play' : 'App Store';
          setPriceFetchError(
            t(
              'premium.noProductsError',
              `Impossibile recuperare i prodotti da ${platformLabel}. Verifica configurazione RevenueCat e ID prodotto.`,
            ),
          );
        }
      } catch (e) {
        console.warn('[Premium] could not fetch StoreKit products:', e);
        if (!cancelled) {
          setPriceFetchError(
            t('premium.priceFetchError', 'Errore nel recupero dei prezzi: ') + (e?.message || e),
          );
        }
      } finally {
        if (!cancelled) setPricesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [paymentPlatform]);
  
  // Countdown for special offer
  const offer = config?.offer;
  const countdown = useCountdown(offer?.expires_at);

  // Build translated features
  const FEATURES = [
    { icon: Ban, text: t('premium.features.noAds'), description: t('premium.features.noAdsDesc') },
    { icon: Camera, text: t('premium.features.aiTransform'), description: t('premium.features.aiTransformDesc') },
    { icon: ChefHat, text: t('premium.features.aiChef'), description: t('premium.features.aiChefDesc') },
    { icon: CalendarRange, text: t('premium.features.mealPlan'), description: t('premium.features.mealPlanDesc') },
    { icon: ShoppingCart, text: t('premium.features.shoppingList'), description: t('premium.features.shoppingListDesc') },
    { icon: Music, text: t('premium.features.moveMusic'), description: t('premium.features.moveMusicDesc') },
    { icon: Crown, text: t('premium.features.premiumBadge'), description: t('premium.features.premiumBadgeDesc') }
  ];

  // Get locale for date formatting
  const getLocale = () => {
    const localeMap = { it: 'it-IT', en: 'en-US', es: 'es-ES', pt: 'pt-BR', de: 'de-DE', fr: 'fr-FR' };
    return localeMap[language] || 'en-US';
  };

  useEffect(() => {
    loadData();
    
    // Check if returning from success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    
    // Refresh data when page becomes visible (returning from another tab/app)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced validation of promo code (500ms after last keystroke)
  useEffect(() => {
    const code = (promoInput || '').trim().toUpperCase();
    if (!code) {
      setPromoResult(null);
      setPromoValidating(false);
      return;
    }
    setPromoValidating(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/promo-codes/validate`,
          { code, plan_type: selectedPlan },
          { withCredentials: true }
        );
        setPromoResult(res.data);
      } catch (err) {
        setPromoResult({ valid: false, error: err.response?.data?.detail || 'Codice non valido' });
      } finally {
        setPromoValidating(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [promoInput, selectedPlan]);

  const loadData = async () => {
    try {
      const configRes = await axios.get(`${API_URL}/api/premium/config`);
      console.log('Premium config loaded:', configRes.data);
      setConfig(configRes.data);
      // Refresh premium status via React Query so every consumer gets fresh data.
      invalidatePremium();
    } catch (err) {
      console.error('Error loading premium data:', err);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/premium/checkout/status/${sessionId}`, { withCredentials: true });
      
      if (res.data.payment_status === 'paid') {
        setShowSuccess(true);
        loadData(); // Refresh status
        // Clear URL params
        window.history.replaceState({}, '', '/premium');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error('Error checking payment:', err);
    }
  };

  const handleCheckout = async (planType) => {
    // Route by platform / admin toggles:
    //  • ios-app / android-app → RevenueCat via Capacitor plugin
    //  • web                   → existing Stripe modal
    if (activeProvider === 'apple' || activeProvider === 'google') {
      // Verify the JS bridge is actually injected before we surface
      // a confusing native dialog. The diagnostic page covers the
      // edge cases.
      if (!isRevenueCatAvailable()) {
        setErrorModal({
          isOpen: true,
          title: t('premium.iapNotActivatedTitle'),
          message: t('premium.iapNotActivatedMessage'),
        });
        return;
      }
      const rcCfg = paymentsConfig?.revenuecat || {};
      const packageId = planType === 'annual'
        ? (rcCfg.package_yearly || '$rc_annual')
        : (rcCfg.package_monthly || '$rc_monthly');
      const customerId = user?.user_id;
      if (!customerId) {
        setErrorModal({
          isOpen: true,
          title: t('premium.iapUserErrorTitle'),
          message: t('premium.iapUserErrorMessage'),
        });
        return;
      }
      setCheckingOut(true);
      try {
        // 1. Bind the current Slimmy user to the RevenueCat customer.
        await rcLogin(customerId, user?.email || '');
        // 2. Trigger the native StoreKit / Play Billing flow.
        const purchase = await rcPurchase(packageId);
        // 3. Server-side double-check via RevenueCat REST API + grant.
        await syncWithBackend(customerId, packageId, purchase.transactionId);
        // 4. Refresh local Premium status.
        await invalidatePremium();
        // Success: use a non-blocking green toast instead of the
        // (red, alert-styled) ErrorModal. We also flip `showSuccess`
        // so the "Welcome Premium" screen takes over immediately.
        toast.success(t('premium.iapSubscriptionActivatedTitle'), {
          description: t('premium.iapSubscriptionActivatedMessage'),
          duration: 4000,
        });
        hapticSuccess();
        setShowSuccess(true);
      } catch (err) {
        // Cancelled by the user is NOT an error — silent close.
        if (err?.code === 'CANCELLED' || /cancel/i.test(err?.message || '')) {
          return;
        }
        hapticError();
        setErrorModal({
          isOpen: true,
          title: t('premium.iapPurchaseFailedTitle'),
          message: err?.message || t('premium.iapPurchaseFailedMessage'),
        });
      } finally {
        setCheckingOut(false);
      }
      return;
    }
    if (activeProvider === 'none') {
      setErrorModal({
        isOpen: true,
        title: t('premium.iapPaymentsOffTitle'),
        message: t('premium.iapPaymentsOffMessage'),
        errorCode: null,
      });
      return;
    }
    // Default: web → Stripe in-app modal.
    setPaymentPlanType(planType);
    setShowPaymentModal(true);
  };

  // Apple-required "Restore Purchases" button — only shown on iOS/Android.
  const handleRestore = async () => {
    if (!isRevenueCatAvailable()) {
      setErrorModal({
        isOpen: true,
        title: t('premium.iapRestoreUnavailableTitle'),
        message: t('premium.iapRestoreUnavailableMessage'),
      });
      return;
    }
    setCheckingOut(true);
    try {
      await rcLogin(user?.user_id, user?.email || '');
      await rcRestore();
      await syncWithBackend(user?.user_id);
      await invalidatePremium();
      toast.success(t('premium.iapRestoredTitle'), {
        description: t('premium.iapRestoredMessage'),
        duration: 4000,
      });
    } catch (err) {
      setErrorModal({
        isOpen: true,
        title: t('premium.iapNoRestoreTitle'),
        message: err?.message || t('premium.iapNoRestoreMessage'),
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCheckoutWithPromo = async (planType) => {
    if (!promoResult?.valid) return;
    setCheckingOut(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/premium/checkout`,
        {
          plan_type: planType,
          origin_url: window.location.origin + '/app',
          promo_code: promoResult.code,
        },
        { withCredentials: true }
      );
      if (res.data?.promo_applied) {
        // Zero-price promo (free_months) — premium granted inline
        setShowSuccess(true);
        loadData();
        setPromoInput('');
        setPromoResult(null);
      } else if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setErrorModal({
        isOpen: true,
        title: 'Errore',
        message: err.response?.data?.detail || 'Impossibile procedere con il pagamento',
        errorCode: null,
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowSuccess(true);
    loadData(); // Refresh status
  };

  const handleStartTrial = async () => {
    try {
      console.log('Starting trial request...');
      const response = await axios.post(`${API_URL}/api/premium/start-trial`, {}, { withCredentials: true });
      console.log('Trial response:', response.data);
      loadData();
      setShowSuccess(true);
    } catch (err) {
      console.error('Trial error full:', err);
      
      let statusCode = 'NETWORK';
      let errorMsg = 'Errore di connessione. Verifica la tua connessione internet.';
      
      if (err.response) {
        // Server responded with error
        statusCode = err.response.status;
        errorMsg = err.response.data?.detail || `Errore server: ${statusCode}`;
      } else if (err.request) {
        // Request made but no response
        errorMsg = 'Il server non risponde. Riprova tra qualche secondo.';
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      const errorCode = `TRIAL_${statusCode}_${Date.now()}`;
      
      if (statusCode === 401 || errorMsg.includes('authenticated') || errorMsg.includes('session')) {
        showError(
          'Sessione Scaduta',
          'La tua sessione è scaduta. Effettua nuovamente il login per continuare.',
          errorCode
        );
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else if (errorMsg.includes('già utilizzato') || errorMsg.includes('Trial already used')) {
        showError(
          'Trial già utilizzato',
          'Hai già utilizzato la prova gratuita in precedenza. Puoi acquistare un abbonamento Premium.',
          errorCode
        );
      } else if (errorMsg.includes('già un utente Premium') || errorMsg.includes('Already premium')) {
        showError(
          'Già Premium',
          'Sei già un utente Premium! Non puoi attivare un trial.',
          errorCode
        );
      } else if (errorMsg.includes('non trovato')) {
        showError(
          'Utente non trovato',
          'Il tuo account non è stato trovato. Prova a rieffettuare il login.',
          errorCode
        );
      } else {
        showError(
          'Errore Attivazione Trial',
          errorMsg,
          errorCode
        );
      }
    }
  };

  // Helper functions for special offer
  const hasActiveOffer = (planType) => {
    if (!offer || !countdown) return false;
    if (planType === 'monthly') return offer.monthly?.enabled;
    if (planType === 'annual') return offer.annual?.enabled;
    return false;
  };

  const copyReferralCode = () => {
    if (status?.referral_code) {
      navigator.clipboard.writeText(status.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E8334A]" />
      </div>
    );
  }

  // Success Modal
  if (showSuccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`}>
        <div className={`max-w-sm w-full rounded-3xl p-8 text-center ${isDark ? 'bg-white/5' : 'bg-white'} shadow-xl`}>
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#E8334A] to-[#F59E0B] flex items-center justify-center">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('premium.welcomePremium')}
          </h1>
          <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('premium.accessExclusive')}
          </p>
          <button
            onClick={() => navigate('/app/dashboard')}
            className="w-full py-4 rounded-xl bg-[#E8334A] text-white font-semibold"
          >
            {t('premium.startExploring')}
          </button>
        </div>
      </div>
    );
  }

  // Already Premium
  if (status?.is_premium) {
    return (
      <div className={`flex flex-col ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`} style={{ minHeight: '100vh', paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))' }}>
        {/* Fixed Header with safe area */}
        <div className={`fixed-header px-4 pb-3 flex items-center gap-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'}`}>
          <button onClick={() => navigate('/app/profilo')} className={`p-2 rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
            <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} size={20} />
          </button>
          <h1 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>Premium</h1>
        </div>

        {/* Content below fixed header */}
        <div className="content-below-header px-4 space-y-4">
          {/* Status Card */}
          <div className="bg-gradient-to-br from-[#E8334A] to-[#F59E0B] rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-8 h-8" />
              <div>
                <h2 className="font-bold text-lg">{t('premium.youArePremium')}</h2>
                <p className="text-white/80 text-sm">
                  {status.premium_lifetime 
                    ? t('premium.lifetimeSubscription')
                    : t('premium.expiresOn', { date: new Date(status.premium_expires_at).toLocaleDateString(getLocale()) })
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Referral & Ambassador Badges — visible to ALL users
              (free, premium monthly/annual, lifetime). Lifetime users
              don't get "1 month free" but still earn ambassador
              badges + leaderboard points for inviting friends, which
              is the gamified reward that makes sense for them. */}
          <div className={`rounded-2xl p-5 ${isDark ? 'bg-white/5' : 'bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#E8334A]/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-[#E8334A]" />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('premium.inviteFriends')}</h3>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {status.premium_lifetime
                    ? t('premium.ambassador.lifetimeSubtitle')
                    : t('premium.getMonthFree', { months: config?.referral_months || 1 })}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <div className={`flex-1 px-4 py-3 rounded-xl font-mono text-center ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}>
                {status.referral_code}
              </div>
              <button
                onClick={copyReferralCode}
                className={`px-4 rounded-xl ${copied ? 'bg-green-500 text-white' : 'bg-[#E8334A] text-white'}`}
              >
                {copied ? <Check size={20} /> : <Copy size={20} />}
              </button>
            </div>

            {status.referral_count > 0 && (
              <p className={`mt-3 text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                <Users className="inline w-4 h-4 mr-1" />
                {t('premium.friendsInvited', { count: status.referral_count })}
              </p>
            )}

            {/* Ambassador tiers progress — derived server-side, so the
                frontend only renders. Each tier shows its own progress
                bar + a "unlocked" check when reached. */}
            {Array.isArray(status.ambassador_tiers) && status.ambassador_tiers.length > 0 && (
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  {t('premium.ambassador.title')}
                </p>
                <div className="space-y-2.5" data-testid="ambassador-tier-progress">
                  {status.ambassador_tiers.map((tier) => {
                    const pct = Math.min(100, Math.round((tier.current / tier.threshold) * 100));
                    const emoji = {
                      ambassador_bronze: '🥉',
                      ambassador_silver: '🥈',
                      ambassador_gold: '🥇',
                      ambassador_diamond: '💎',
                    }[tier.code] || '🏆';
                    return (
                      <div key={tier.code} className="flex items-center gap-3" data-testid={`tier-${tier.code}`}>
                        <span className="text-xl flex-shrink-0">{emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {tier.label}
                            </span>
                            <span className={`text-[11px] tabular-nums flex-shrink-0 ${tier.unlocked ? 'text-green-500 font-semibold' : (isDark ? 'text-white/50' : 'text-gray-500')}`}>
                              {tier.unlocked ? `✓ ${t('premium.ambassador.unlocked')}` : `${Math.min(tier.current, tier.threshold)}/${tier.threshold}`}
                            </span>
                          </div>
                          <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${pct}%`,
                                background: tier.unlocked
                                  ? 'linear-gradient(90deg, #16A34A 0%, #22D3EE 100%)'
                                  : 'linear-gradient(90deg, #E8334A 0%, #FF6B7B 100%)',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Features */}
          <div className={`rounded-2xl p-5 ${isDark ? 'bg-white/5' : 'bg-white'}`}>
            <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('premium.yourFeatures')}</h3>
            <div className="space-y-3">
              {FEATURES.map((feature, i) => (
                <div key={feature.text || `feature-${i}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E8334A]/20 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-[#E8334A]" />
                  </div>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not Premium - Show plans
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`}>
      {/* Fixed Header */}
      <div className={`fixed-header px-4 pt-3 pb-3 flex items-center gap-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'}`}>
        <button onClick={() => navigate('/app/profilo')} className={`p-2 rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} size={18} />
        </button>
        <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('premium.title')}</h1>
      </div>

      {/* Scrollable Content */}
      <div className="content-below-header px-4 pb-24 space-y-4">
        {/* Hero - Design premium compatto */}
        <div className="relative bg-gradient-to-br from-[#E8334A] via-[#D9234A] to-[#F59E0B] rounded-2xl p-5 text-white text-center overflow-hidden shadow-lg shadow-[#E8334A]/30">
          {/* Decorazioni */}
          <div className="absolute top-0 left-0 w-20 h-20 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2" />
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white/40 rounded-full animate-pulse" />
          
          <div className="relative z-10">
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold mb-1">{t('premium.slimmyPremium')}</h2>
            <p className="text-white/90 text-xs">{t('premium.unlockPotential')}</p>
          </div>
        </div>

        {/* Special Offer Banner — only on Web (Stripe). Native stores
            have their own intro pricing handled by Apple/Google and the
            admin offer prices are not honored there, so we hide the
            whole promo card on iOS/Android. */}
        {offer && countdown && !isNativeStore && (
          <div className="rounded-3xl p-5 bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 text-white relative overflow-hidden shadow-lg shadow-green-500/30">
            {/* Decorazioni animate */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="absolute top-4 left-4">
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
            </div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Clock className="w-5 h-5" />
                <span className="font-bold text-lg">{offer.title}</span>
              </div>
              {offer.description && (
                <p className="text-white/90 text-sm text-center mb-4">{offer.description}</p>
              )}
              
              {/* Countdown - Design migliorato */}
              <div className="grid grid-cols-4 gap-2 text-center mb-5">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl py-3 px-2">
                  <div className="text-2xl font-bold tabular-nums">{String(countdown.days).padStart(2, '0')}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-1">{t('premium.countdown.days')}</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl py-3 px-2">
                  <div className="text-2xl font-bold tabular-nums">{String(countdown.hours).padStart(2, '0')}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-1">{t('premium.countdown.hours')}</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl py-3 px-2">
                  <div className="text-2xl font-bold tabular-nums">{String(countdown.minutes).padStart(2, '0')}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-1">{t('premium.countdown.minutes')}</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl py-3 px-2">
                  <div className="text-2xl font-bold tabular-nums">{String(countdown.seconds).padStart(2, '0')}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-1">{t('premium.countdown.seconds')}</div>
                </div>
              </div>
              
              {/* Offer Prices - Design migliorato */}
              <div className="space-y-2">
                {offer.monthly?.enabled && (
                  <div className="flex items-center justify-between bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3">
                    <span className="font-medium">{t('premium.monthly')}</span>
                    <div className="flex items-center gap-2">
                      <span className="line-through text-white/60 text-sm">{getCurrencySymbol()}{formatPrice(offer.monthly.original_price)}</span>
                      <span className="font-bold text-xl">{getCurrencySymbol()}{formatPrice(offer.monthly.price)}</span>
                    </div>
                  </div>
                )}
                {offer.annual?.enabled && (
                  <div className="flex items-center justify-between bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3">
                    <span className="font-medium">{t('premium.annual')}</span>
                    <div className="flex items-center gap-2">
                      <span className="line-through text-white/60 text-sm">{getCurrencySymbol()}{formatPrice(offer.annual.original_price)}</span>
                      <span className="font-bold text-xl">{getCurrencySymbol()}{formatPrice(offer.annual.price)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Native store fetch error — shown when the RC plugin returned
            no products (typically: Google Play product IDs missing or
            RevenueCat offering not configured). We deliberately do NOT
            show hardcoded prices in this case — per product spec. */}
        {isNativeStore && priceFetchError && !pricesLoading && (
          <div
            className="rounded-xl p-4 border-2 border-amber-500/40 bg-amber-500/10"
            data-testid="premium-no-products-error"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className={`flex-1 text-sm ${isDark ? 'text-white/90' : 'text-gray-800'}`}>
                <strong className="block mb-1 text-amber-500">
                  {t('premium.priceUnavailableTitle', 'Prezzi non disponibili')}
                </strong>
                {priceFetchError}
              </div>
            </div>
          </div>
        )}

        {/* Plan Selector - Design più moderno */}
        <div className={`rounded-xl p-1 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                selectedPlan === 'monthly'
                  ? 'bg-[#E8334A] text-white shadow-lg shadow-[#E8334A]/30'
                  : isDark ? 'text-white/60 hover:text-white/80' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('premium.monthly')}
            </button>
            <button
              onClick={() => setSelectedPlan('annual')}
              className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                selectedPlan === 'annual'
                  ? 'bg-[#E8334A] text-white shadow-lg shadow-[#E8334A]/30'
                  : isDark ? 'text-white/60 hover:text-white/80' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('premium.annual')}
            </button>
          </div>
        </div>

        {/* Selected Plan Card - Design Premium compatto */}
        <div className={`rounded-2xl p-4 relative overflow-hidden transition-all duration-300 ${
          isDark ? 'bg-white/5' : 'bg-white'
        } ${
          selectedPlan === 'annual' && !hasActiveOffer(selectedPlan) 
            ? 'ring-2 ring-[#E8334A] shadow-lg shadow-[#E8334A]/10' 
            : hasActiveOffer(selectedPlan) 
              ? 'ring-2 ring-green-500 shadow-lg shadow-green-500/10'
              : 'shadow-md'
        }`}>
          {/* Annual saving badge — computed dynamically from RC prices.
              Hidden if either price is missing (no hardcoded fallback)
              or if the annual is not actually cheaper than 12×monthly. */}
          {selectedPlan === 'annual' && !hasActiveOffer(selectedPlan) && (() => {
            const discount = getAnnualDiscountPercent();
            if (discount == null) return null;
            return (
              <div
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#E8334A] to-[#F59E0B] text-white text-[10px] font-bold mb-3"
                data-testid="premium-annual-discount-badge"
              >
                <Sparkles className="w-3 h-3" />
                {t('premium.saveDiscount', { percent: discount })}
              </div>
            );
          })()}
          {hasActiveOffer(selectedPlan) && (
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] font-bold mb-3">
              <Gift className="w-3 h-3" />
              {t('premium.specialOffer')}
            </div>
          )}
          
          {/* Prezzo */}
          {(() => {
            const m = storePrices?.monthly;
            const a = storePrices?.annual;
            // Storefront detection from the currency code(s) the SDK
            // returned. Apple does NOT expose Storefront directly here,
            // but the currency uniquely identifies it.
            const STOREFRONT_BY_CURRENCY = {
              EUR: 'Eurozone (IT/DE/FR/ES/PT/…)',
              USD: 'United States',
              GBP: 'United Kingdom',
              BRL: 'Brasil',
              MXN: 'México',
              JPY: 'Japan',
            };
            const code = a?.currencyCode || m?.currencyCode || null;
            const detectedStorefront = code
              ? (STOREFRONT_BY_CURRENCY[code] || `Sconosciuto (${code})`)
              : null;
            console.log('PREMIUM PRICE DEBUG', {
              monthlyPriceString: m?.priceString ?? null,
              monthlyPrice: m?.price ?? null,
              monthlyCurrencyCode: m?.currencyCode ?? null,
              annualPriceString: a?.priceString ?? null,
              annualPrice: a?.price ?? null,
              annualCurrencyCode: a?.currencyCode ?? null,
              detectedStorefront,
              selectedLocale: (typeof navigator !== 'undefined' ? navigator.language : null),
              appLanguage: language,
            });
            return null;
          })()}
          <div className="flex items-baseline gap-2 mb-1 min-h-[3rem]">
            {!isPriceReady(selectedPlan) ? (
              <span
                className="inline-flex items-center gap-2 text-base text-gray-400"
                data-testid="premium-price-loader"
              >
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="sr-only">{t('common.loading') || 'Loading…'}</span>
              </span>
            ) : (
              <>
                {/* Strike-through original price only on Web (Stripe offers).
                    Native stores have their own intro pricing handled by
                    Apple/Google — we never overlay our admin offers there. */}
                {hasActiveOffer(selectedPlan) && !isNativeStore && (
                  <span className={`text-xl line-through ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    {getCurrencySymbol()}{formatPrice(selectedPlan === 'monthly' ? getPrice('monthly') : getPrice('annual'))}
                  </span>
                )}
                <span className={`text-4xl font-bold tracking-tight ${
                  hasActiveOffer(selectedPlan) && !isNativeStore ? 'text-green-500' : isDark ? 'text-white' : 'text-gray-900'
                }`}
                  data-testid={`premium-price-${selectedPlan}`}
                >
                  {getPriceString(selectedPlan)}
                </span>
                <span className={`text-base ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {selectedPlan === 'monthly' ? t('premium.perMonth') : t('premium.perYear')}
                </span>
              </>
            )}
          </div>
          
          {selectedPlan === 'annual' && isPriceReady('annual') && (() => {
            const perMonth = getPerMonthString();
            if (!perMonth) return null;
            return (
              <p className={`text-xs mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}
                data-testid="premium-permonth-equivalent"
              >
                {t('premium.only')} {perMonth}{t('premium.perMonth')}
              </p>
            );
          })()}
          {selectedPlan === 'monthly' && (
            <div className="mb-3" />
          )}

          {/* Promo code input — hidden when admin disables it globally */}
          {promoCodesEnabled && (
          <div className={`mt-3 mb-3 rounded-xl p-3 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-4 h-4 text-[#E8334A]" />
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('premium.promoCode') || 'Hai un codice promo?'}
              </span>
            </div>
            <input
              type="text"
              data-testid="premium-promo-input"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value.replace(/\s/g, '').toUpperCase())}
              placeholder={t('premium.promoCodePlaceholder') || 'Es. LAUNCH29'}
              maxLength={40}
              className={`w-full px-3 py-2 rounded-lg text-sm outline-none ${
                isDark ? 'bg-white/5 text-white border border-white/10 focus:border-[#E8334A]/50' : 'bg-white text-gray-900 border border-gray-200 focus:border-[#E8334A]/50'
              }`}
            />
            {promoValidating && (
              <p className={`text-xs mt-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('premium.promoValidating') || 'Validazione codice...'}
              </p>
            )}
            {!promoValidating && promoResult?.valid && (
              <div className="mt-2 p-2 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-green-500 text-sm font-medium">
                  ✓ {promoResult.type === 'free_months'
                    ? t('premium.promoFreeMonths', { months: promoResult.free_months }) || `${promoResult.free_months} mesi gratis`
                    : t('premium.promoDiscount', { price: promoResult.final_price, saved: promoResult.base_price }) || `Paghi €${promoResult.final_price} invece di €${promoResult.base_price} (risparmi €${promoResult.amount_saved})`}
                </p>
              </div>
            )}
            {!promoValidating && promoResult && !promoResult.valid && (
              <p className="text-red-500 text-xs mt-2">✗ {promoResult.error}</p>
            )}
            {promoResult?.valid && (
              <button
                onClick={() => handleCheckoutWithPromo(selectedPlan)}
                disabled={checkingOut}
                data-testid="premium-promo-checkout-btn"
                className="w-full mt-3 py-2.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold text-sm shadow-md shadow-green-500/30 disabled:opacity-50 active:scale-[0.98] transition"
              >
                {checkingOut
                  ? (t('premium.loading') || 'Caricamento...')
                  : promoResult.type === 'free_months'
                    ? (t('premium.activateFree') || 'Attiva Premium gratis')
                    : `${t('premium.payWithCode') || 'Paga con codice'} — €${promoResult.final_price}`}
              </button>
            )}
          </div>
          )}

          <button
            onClick={() => handleCheckout(selectedPlan)}
            disabled={checkingOut || activeProvider === 'none' || !isPriceReady(selectedPlan)}
            data-testid="premium-checkout-btn"
            className={`w-full py-3 rounded-xl text-white font-bold text-base disabled:opacity-50 transition-all duration-300 active:scale-[0.98] ${
              hasActiveOffer(selectedPlan) 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg shadow-green-500/30' 
                : 'bg-gradient-to-r from-[#E8334A] to-[#D9234A] shadow-lg shadow-[#E8334A]/30'
            }`}
          >
            {checkingOut ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('premium.loading')}
              </span>
            ) : hasActiveOffer(selectedPlan) ? (
              <span className="flex items-center justify-center gap-2">
                <Gift className="w-4 h-4" />
                {t('premium.grabOffer')}
              </span>
            ) : activeProvider === 'apple' ? (
              <span className="flex items-center justify-center gap-2">
                <span style={{ fontSize: 18 }}></span>
                {t('premium.continueWithApple')}
              </span>
            ) : activeProvider === 'google' ? (
              <span className="flex items-center justify-center gap-2">
                <span>▶</span>
                {t('premium.continueWithGoogle')}
              </span>
            ) : activeProvider === 'none' ? (
              t('premium.paymentsUnavailable')
            ) : (
              t('premium.subscribeNow')
            )}
          </button>

          {/* Apple/Google require a visible "Restore Purchases" link
              for users who reinstall the app or switch device. */}
          {(activeProvider === 'apple' || activeProvider === 'google') && (
            <button
              onClick={handleRestore}
              disabled={checkingOut}
              data-testid="premium-restore-btn"
              className={`w-full mt-2 py-2 rounded-xl text-sm font-medium underline-offset-2 ${
                isDark ? 'text-white/70 hover:text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              {t('premium.restorePurchases')}
            </button>
          )}

          {/* Legal Footer — required by App Store Review Guideline 3.1.2(c).
              EULA (Apple standard) + Privacy Policy. Opens via Capacitor
              in-app browser (SFSafariViewController) on iOS. */}
          <div className={`mt-3 pt-3 border-t flex items-center justify-center gap-2 flex-wrap ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-testid="paywall-legal-footer">
            <button
              type="button"
              data-testid="paywall-terms-link"
              onClick={async () => {
                const url = 'https://slimmy.app/terms';
                try {
                  const { Browser } = await import('@capacitor/browser');
                  if (!Browser?.open) throw new Error('Browser plugin not available');
                  await Browser.open({ url, presentationStyle: 'fullscreen' });
                } catch {
                  window.open(url, '_blank');
                }
              }}
              className={`text-xs font-medium px-3 py-2 rounded-xl border transition-all active:scale-95 ${
                isDark
                  ? 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('premium.termsOfUse')}
            </button>
            <button
              type="button"
              data-testid="paywall-privacy-link"
              onClick={async () => {
                const url = `${window.location.origin}/privacy`;
                try {
                  const { Browser } = await import('@capacitor/browser');
                  if (!Browser?.open) throw new Error('Browser plugin not available');
                  await Browser.open({ url, presentationStyle: 'fullscreen' });
                } catch {
                  window.open('/privacy', '_blank');
                }
              }}
              className={`text-xs font-medium px-3 py-2 rounded-xl border transition-all active:scale-95 ${
                isDark
                  ? 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('premium.privacyPolicy')}
            </button>
          </div>
        </div>

        {/* Trial Banner - compatto */}
        {!status?.trial_used && (
          <div className={`rounded-2xl p-4 border-2 border-dashed ${
            isDark ? 'border-[#E8334A]/40 bg-[#E8334A]/5' : 'border-[#E8334A]/30 bg-[#E8334A]/5'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#E8334A]/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#E8334A]" />
              </div>
              <div className="flex-1">
                <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('premium.freeTrial', { days: config?.trial_days || 30 })}
                </h3>
                <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('premium.tryPremium')}
                </p>
              </div>
            </div>
            <button
              onClick={handleStartTrial}
              data-testid="start-trial-btn"
              className="w-full py-2.5 rounded-lg bg-[#E8334A]/10 text-[#E8334A] font-semibold text-sm border border-[#E8334A]/30 hover:bg-[#E8334A]/20 transition-all"
            >
              {t('premium.startTrial')}
            </button>
          </div>
        )}

        {/* Features List - compatto */}
        <div className={`rounded-2xl p-4 ${isDark ? 'bg-white/5' : 'bg-white'} shadow-sm`}>
          <h3 className={`font-bold text-base mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('premium.whatIncluded')}</h3>
          <div className="space-y-2.5">
            {FEATURES.map((feature, i) => (
              <div key={feature.text || `feat-${i}`} className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isDark ? 'bg-[#E8334A]/20' : 'bg-gradient-to-br from-[#E8334A]/10 to-[#F59E0B]/10'
                }`}>
                  <feature.icon className="w-4 h-4 text-[#E8334A]" />
                </div>
                <div className="flex-1">
                  <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{feature.text}</h4>
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{feature.description}</p>
                </div>
                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
        errorCode={errorModal.errorCode}
      />
      
      {/* In-App Payment Modal */}
      <StripePaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        planType={paymentPlanType}
        referralCode={null}
        onSuccess={handlePaymentSuccess}
        currency={userCurrency}
        currencySymbol={getCurrencySymbol()}
        price={getPrice(paymentPlanType)}
      />
    </div>
  );
}
